import { useEffect, useRef, useState } from 'react'

import { formatDate, formatTime } from '../lib/format'

/**
 * Мессенджер. Один компонент на оба контура — кабинет клиента и программу.
 *
 * Сделан общим намеренно. Переписка одна и та же, и разойдись эти два экрана,
 * однажды одна сторона увидела бы не то, что другая. Всё, чем они отличаются,
 * передаётся снаружи: как получить список, как открыть беседу, как отправить.
 *
 * Слева список бесед, справа переписка — как в любом мессенджере, узнаётся
 * без объяснений. Новое сообщение прокручивает разговор вниз; пока окно
 * открыто, список и переписка сами обновляются раз в двадцать секунд.
 */

export interface QuotedView {
  id: number
  author_name: string | null
  body: string
  from_client: boolean
  conversation_title: string | null
}

export interface ChatMessageView {
  id: number
  body: string
  created_at: string
  author_name: string | null
  from_client: boolean
  file_name: string | null
  file_size: number | null
  reply_to: QuotedView | null
  forwarded_from: QuotedView | null
  edited_at: string | null
  editable: boolean
}

export interface ConversationView {
  id: number
  title: string
  preview: string | null
  last_message_at: string | null
  last_from_client: boolean
  unread: number
  ticket_id: number | null
  /** Обращение или заявка — от этого зависит, в какой раздел вести */
  ticket_kind: 'request' | 'order' | null
  messages: ChatMessageView[]
}

export interface MessengerProps {
  /** Список бесед. Свежие сверху — так их и присылает сервер. */
  conversations: ConversationView[]
  loading?: boolean
  /** Открытая беседа с репликами. null — ни одна не выбрана. */
  opened: ConversationView | null
  openedId: number | null
  onOpen: (id: number) => void
  onSend: (
    body: string,
    file: File | null,
    opts: { replyTo: number | null; forwardOf: number | null },
  ) => Promise<void>
  onEdit: (messageId: number, body: string) => Promise<void>
  onDownload: (messageId: number, name: string) => void
  /** Открыть обращение или заявку, к которым привязана беседа. */
  onOpenTicket?: (ticketId: number, kind: 'request' | 'order' | null) => void
  /** Чьи реплики показывать справа, как свои. */
  mineIsClient: boolean
  /** Кнопка «написать» — только в кабинете: разговор начинает клиент. */
  header?: React.ReactNode
  emptyHint: string
  error?: string
}

export default function Messenger({
  conversations,
  loading,
  opened,
  openedId,
  onOpen,
  onSend,
  onEdit,
  onDownload,
  onOpenTicket,
  mineIsClient,
  header,
  emptyHint,
  error,
}: MessengerProps) {
  const [text, setText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  // На что отвечаем и что пересылаем — показывается над полем ввода, чтобы
  // человек видел, к чему относится то, что он сейчас пишет.
  const [replyTo, setReplyTo] = useState<ChatMessageView | null>(null)
  const [forwarding, setForwarding] = useState<ChatMessageView | null>(null)
  const [editing, setEditing] = useState<ChatMessageView | null>(null)
  const bottom = useRef<HTMLDivElement>(null)

  const messages = opened?.messages ?? []

  // Новая реплика — вниз, к ней и прокручиваем
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [messages.length, openedId])

  // Сменили беседу — черновик и все заготовки от прошлой уезжать не должны.
  // Кроме пересылки: её как раз и переносят в другую беседу.
  useEffect(() => {
    setText('')
    setFile(null)
    setReplyTo(null)
    setEditing(null)
  }, [openedId])

  function startEdit(m: ChatMessageView) {
    setEditing(m)
    setText(m.body)
    setReplyTo(null)
    setForwarding(null)
  }

  function cancelEdit() {
    setEditing(null)
    setText('')
  }

  async function send() {
    if (busy) return
    if (editing) {
      if (!text.trim()) return
      setBusy(true)
      try {
        await onEdit(editing.id, text.trim())
        cancelEdit()
      } finally {
        setBusy(false)
      }
      return
    }
    if (!text.trim() && !file && !forwarding) return
    setBusy(true)
    try {
      await onSend(text.trim(), file, {
        replyTo: replyTo?.id ?? null,
        forwardOf: forwarding?.id ?? null,
      })
      setText('')
      setFile(null)
      setReplyTo(null)
      setForwarding(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ms">
      <aside className="ms-list">
        {header}
        {loading && <p className="empty">Загрузка…</p>}
        {!loading && conversations.length === 0 && <p className="empty">{emptyHint}</p>}
        {conversations.map((c) => (
          <button
            key={c.id}
            className={`ms-item${c.id === openedId ? ' on' : ''}`}
            onClick={() => onOpen(c.id)}
          >
            <div className="ms-item-head">
              <span className="ms-item-title">{c.title}</span>
              {c.unread > 0 && <span className="ms-badge">{c.unread}</span>}
            </div>
            <div className="ms-item-preview">{c.preview ?? 'Пока пусто'}</div>
            {c.last_message_at && (
              <div className="ms-item-time">
                {formatDate(c.last_message_at)} {formatTime(c.last_message_at)}
              </div>
            )}
          </button>
        ))}
      </aside>

      <section className="ms-room">
        {error && <div className="error">{error}</div>}

        {!opened ? (
          <p className="empty" style={{ paddingTop: 40 }}>
            Выберите беседу слева
          </p>
        ) : (
          <>
            <div className="ms-room-head">
              <span>{opened.title}</span>
              {/* Ссылка на саму заявку: из разговора часто нужно посмотреть,
                  что в ней написано и на каком она этапе. */}
              {opened.ticket_id !== null && onOpenTicket && (
                <button
                  className="btn small"
                  onClick={() => onOpenTicket(opened.ticket_id!, opened.ticket_kind)}
                >
                  {opened.ticket_kind === 'order' ? 'Открыть заявку →' : 'Открыть обращение →'}
                </button>
              )}
            </div>

            <div className="ms-thread">
              {messages.length === 0 && (
                <p className="empty" style={{ padding: '18px 0' }}>
                  Здесь пока ничего не написано. Начните разговор.
                </p>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`tk-msg ${
                    m.from_client === mineIsClient ? 'tk-msg-mine' : 'tk-msg-theirs'
                  }`}
                >
                  <div className="tk-msg-head">
                    {m.author_name ?? (m.from_client ? 'Клиент' : 'Бизнес Эра')}
                    <span className="tk-msg-time">
                      {formatDate(m.created_at)} {formatTime(m.created_at)}
                      {m.edited_at && ' · изменено'}
                    </span>
                  </div>

                  {m.forwarded_from && (
                    <div className="ms-quote forwarded">
                      <div className="ms-quote-head">
                        Переслано: {m.forwarded_from.author_name ?? 'без автора'}
                        {m.forwarded_from.conversation_title &&
                          ` · ${m.forwarded_from.conversation_title}`}
                      </div>
                      {m.forwarded_from.body}
                    </div>
                  )}

                  {m.reply_to && (
                    <div className="ms-quote">
                      <div className="ms-quote-head">
                        {m.reply_to.author_name ?? 'без автора'}
                      </div>
                      {m.reply_to.body}
                    </div>
                  )}

                  {m.body && <div className="tk-msg-body">{m.body}</div>}

                  {m.file_name && (
                    <button
                      type="button"
                      className="tk-file"
                      style={{ marginTop: 6 }}
                      title={`${Math.max(1, Math.round((m.file_size ?? 0) / 1024))} КБ`}
                      onClick={() => onDownload(m.id, m.file_name!)}
                    >
                      📎 {m.file_name}
                    </button>
                  )}

                  <div className="ms-actions">
                    <button className="link-btn" onClick={() => setReplyTo(m)}>
                      Ответить
                    </button>
                    <button className="link-btn" onClick={() => setForwarding(m)}>
                      Переслать
                    </button>
                    {m.editable && (
                      <button className="link-btn" onClick={() => startEdit(m)}>
                        Изменить
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottom} />
            </div>

            <div className="ms-write">
              {editing && (
                <Draft
                  title="Изменяете своё сообщение"
                  body={editing.body}
                  onCancel={cancelEdit}
                />
              )}
              {replyTo && (
                <Draft
                  title={`Ответ: ${replyTo.author_name ?? ''}`}
                  body={replyTo.body}
                  onCancel={() => setReplyTo(null)}
                />
              )}
              {forwarding && (
                <Draft
                  title={`Переслать: ${forwarding.author_name ?? ''}`}
                  body={forwarding.body}
                  onCancel={() => setForwarding(null)}
                  hint="Выберите беседу слева — перешлём туда"
                />
              )}

              <textarea
                rows={2}
                value={text}
                placeholder={
                  editing
                    ? 'Поправьте текст. Сохранить — Ctrl+Enter'
                    : 'Напишите сообщение. Отправить — Ctrl+Enter'
                }
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault()
                    void send()
                  }
                }}
              />
              <div className="ms-write-actions">
                {!editing && (
                  <label className="btn small" style={{ cursor: 'pointer' }}>
                    {file ? `📎 ${file.name}` : '📎 Файл'}
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    />
                  </label>
                )}
                {file && !editing && (
                  <button type="button" className="btn small" onClick={() => setFile(null)}>
                    Убрать файл
                  </button>
                )}
                <div style={{ flex: 1 }} />
                <button
                  className="btn primary"
                  disabled={
                    busy ||
                    (editing ? !text.trim() : !text.trim() && !file && !forwarding)
                  }
                  onClick={() => void send()}
                >
                  {busy ? 'Отправляем…' : editing ? 'Сохранить' : 'Отправить'}
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  )
}

/** Заготовка над полем ввода: на что отвечаем, что пересылаем, что правим. */
function Draft({
  title,
  body,
  onCancel,
  hint,
}: {
  title: string
  body: string
  onCancel: () => void
  hint?: string
}) {
  return (
    <div className="ms-draft">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ms-quote-head">{title}</div>
        <div className="ms-draft-body">{body}</div>
        {hint && <div className="hint-inline">{hint}</div>}
      </div>
      <button className="btn small" onClick={onCancel}>
        Отменить
      </button>
    </div>
  )
}
